import { useEffect, useRef } from 'react';
import { Form, Formik, FormikConfig, FormikValues } from 'formik';
import { find } from 'lodash';

import { RadioGroupControl } from 'formik-chakra-ui';
import {
  Heading,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  VStack,
  Flex,
} from '@chakra-ui/react';
import PageHero from '@inspire/ui/chakra/layout/PageHero';
import { PisteFeedbackFormSchema } from '@inspire/data/schemas/piste-feedback';
import {
  validateSchema,
  initFormikValuesFromSchema,
} from '@inspire/data/helpers/schemas';

import { useUser, useSubmitPisteFeedback, useMyProfile } from 'lib/hooks';
import FavoriteButton from 'components/piste/FavoriteButton';
import SubmitButton from 'components/forms/SubmitButton';
import PisteFeedbackForSinglePiste from 'components/piste/PisteFeedbackForSinglePiste';

const PisteFeedbackForm = ({ piste, closeModal, refetchPiste }) => {
  const { submitPisteFeedbackAsync } = useSubmitPisteFeedback({
    onSuccess: closeModal,
  });
  const schema = PisteFeedbackFormSchema.pick(
    'knewThePiste',
    'consideredThePiste'
  );
  const formik: FormikConfig<FormikValues> = {
    initialValues: initFormikValuesFromSchema(schema),
    enableReinitialize: true,
    validate: (values) => validateSchema(schema, values),
    onSubmit: async (values) => {
      await submitPisteFeedbackAsync({
        input: {
          pisteId: piste._id,
          pisteName: piste.name,
          ...schema.clean(values),
        },
      });
      await refetchPiste();
    },
  };
  return (
    <Formik {...formik}>
      {({ values }) => (
        <Form>
          <VStack spacing={5} pb={3}>
            <RadioGroupControl
              name="knewThePiste"
              label={`Connaissais-tu "${piste.name}" avant de venir sur INSPIRE ?`}
              stackProps={{ direction: 'column' }}
              isRequired
            >
              <Radio value="true">Oui</Radio>
              <Radio value="false">Non</Radio>
            </RadioGroupControl>

            {values.knewThePiste === 'true' && (
              <RadioGroupControl
                name="consideredThePiste"
                label={`Envisageais-tu déjà "${piste.name}" avant de venir sur INSPIRE ?`}
                stackProps={{ direction: 'column' }}
                isRequired
              >
                <Radio value="true">Oui</Radio>
                <Radio value="false">Non</Radio>
              </RadioGroupControl>
            )}
            <SubmitButton>Valider</SubmitButton>
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

const PisteHeader = ({ piste, refetchPiste }) => {
  const modalRef = useRef();
  const { isLyceen } = useUser();
  const { myProfile } = useMyProfile();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isPisteFavorite = !!(
    isLyceen &&
    piste &&
    find(myProfile?.favoritePistes, { id: piste._id })
  );

  useEffect(() => {
    if (!!piste && isPisteFavorite && !piste?.pisteFeedback?.hasFeedback) {
      onOpen();
    }
  }, [isPisteFavorite, piste, onOpen]);

  return (
    <>
      <PageHero bgImage={piste?.coverPicture}>
        <Heading color="white" mt={1}>
          {piste?.name}
        </Heading>
        {isLyceen && (
          <Flex
            direction={['column', null, 'row']}
            align="center"
            justifyContent="space-between"
            w="full"
          >
            <FavoriteButton
              pisteId={piste?._id}
              gtmLabel="Clic sur le bouton (page piste)"
              mb={[5, null, undefined]}
            />
            {myProfile?.algoResults?.find((p) => p.pisteId === piste?._id) && (
              <PisteFeedbackForSinglePiste pisteId={piste?._id} />
            )}
          </Flex>
        )}
      </PageHero>
      <Modal
        finalFocusRef={modalRef}
        isOpen={isOpen}
        onClose={onClose}
        preserveScrollBarGap
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{piste?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <PisteFeedbackForm
              piste={piste}
              closeModal={onClose}
              refetchPiste={refetchPiste}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PisteHeader;
